import { useState, useEffect, useMemo, useRef } from 'react';
import { http } from '@/lib/http';
import QueryString from 'qs';

export const useDependentOptions = ({
  name,
  dependsOn = [],
  fetchConfig,
  formValues,
  clearOnDependencyChange = true,
  onClear,
  isMulti = false,
}) => {
  // Handle both old format (string[]) and new format ({ field, label }[])
  const normalizedDependencies = useMemo(() => {
    return dependsOn.map((dep) => {
      if (typeof dep === 'string') {
        return { field: dep, label: dep };
      }
      return dep;
    });
  }, [dependsOn]);

  // Extract field names for compatibility
  const dependencyFieldNames = useMemo(() => {
    return normalizedDependencies.map((dep) => dep.field);
  }, [normalizedDependencies]);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if all dependencies are satisfied (memoize dependency values)
  const dependencyValues = useMemo(() => {
    return dependencyFieldNames.reduce((acc, fieldName) => {
      acc[fieldName] = formValues[fieldName];
      return acc;
    }, {});
  }, [dependencyFieldNames, formValues]);

  const hasAllDependencies = useMemo(() => {
    return dependencyFieldNames.every((fieldName) => {
      const value = dependencyValues[fieldName];
      return value !== null && value !== undefined && value !== '';
    });
  }, [dependencyFieldNames, dependencyValues]);

  // Get missing dependencies with labels
  const missingDependencies = useMemo(() => {
    return normalizedDependencies.filter((dep) => {
      const value = dependencyValues[dep.field];
      return value === null || value === undefined || value === '';
    });
  }, [normalizedDependencies, dependencyValues]);

  // Track previous dependency values for change detection
  const prevDependencyValues = useRef({});

  // Detect dependency changes with debouncing
  const dependencyChanged = useMemo(() => {
    const changed = dependencyFieldNames.some(
      (fieldName) =>
        prevDependencyValues.current[fieldName] !== dependencyValues[fieldName]
    );

    // Only update if changed to prevent unnecessary recalculations
    if (changed) {
      prevDependencyValues.current = { ...dependencyValues };
    }

    return changed;
  }, [dependencyFieldNames, dependencyValues]);

  // Fetch options when dependencies change
  useEffect(() => {
    if (dependencyChanged) {
      // Always clear the current field value when dependencies change
      if (clearOnDependencyChange && onClear) {
        onClear(getEmptyValue());
      }
      if (hasAllDependencies) {
        // Fetch new options based on updated dependencies
        fetchOptions();
      } else {
        // Clear options when dependencies are missing
        setOptions([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dependencyChanged,
    hasAllDependencies,
    clearOnDependencyChange,
    onClear,
    isMulti,
  ]);

  const fetchOptions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = fetchConfig.buildParams(formValues);
      const endpoint = `${fetchConfig.endpoint}?${QueryString.stringify(
        params,
        { arrayFormat: 'brackets' }
      )}`;

      const response = await http.get(endpoint);
      const processedOptions = fetchConfig.processResponse
        ? fetchConfig.processResponse(response)
        : response.data?.autocomplete_values || [];

      setOptions(processedOptions);
    } catch (err) {
      console.error(`Error fetching options for ${name}:`, err);
      setError(err);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getEmptyValue = () => {
    return isMulti ? [] : '';
  };

  return {
    options,
    isLoading,
    hasAllDependencies,
    missingDependencies,
    error,
    refetch: fetchOptions,
  };
};
